/**
 * Phone normalization helpers — keep the app side and the Edge Function
 * side in agreement. The DB CHECK constraint on relay.sms_opt_outs.phone
 * (and relay.sms_phone_numbers.phone_number) is `^\+[1-9]\d{1,14}$`, so
 * any phone that flows into a Supabase table must match.
 */

/**
 * Normalize a free-form phone string to E.164. Mirrors the same logic as
 * `normalizeE164` in supabase/functions/relay-send-sms/index.ts so the
 * client-side preview matches what the Edge Function will store.
 *
 * Strategy: strip non-digits; if 10 digits, prepend '+1' (NANP default).
 * If 11 starting with 1, prepend '+'. Otherwise prepend '+' and trust the
 * caller has the country code right.
 */
export function normalizeE164(input: string): string {
  const digits = input.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  if (input.trim().startsWith("+")) return `+${digits}`;
  return `+${digits}`;
}

/**
 * Strict E.164 validator — must match the DB CHECK regex exactly.
 */
export function isValidE164(phone: string): boolean {
  return /^\+[1-9]\d{1,14}$/.test(phone);
}

/**
 * Pretty-print E.164 for UI display. NANP numbers ('+1XXXXXXXXXX') get
 * formatted as '+1 (XXX) XXX-XXXX'. Other E.164 numbers are returned as-is.
 */
export function formatPhoneForDisplay(phone: string): string {
  if (!isValidE164(phone)) return phone;
  if (phone.startsWith("+1") && phone.length === 12) {
    const area = phone.slice(2, 5);
    const prefix = phone.slice(5, 8);
    const line = phone.slice(8, 12);
    return `+1 (${area}) ${prefix}-${line}`;
  }
  return phone;
}
